import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Fade from "@mui/material/Fade";
import { Box, Typography, styled } from "@mui/material";
import Button from "../../../components/Button";
import CustomTextField from "../../../components/Textfield";
import { getSx } from "../../../components/Modal/style";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { addFaq, editFaq } from "../../../http/auth";
import { useFormik } from "formik";
import Spinner from "../../../components/Spinner";
import { showToast } from "../../../store/slices/common";
import CustomTextArea from "../../../components/Textarea";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

const CustomDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    width: "600px",
    padding: "30px",
    borderRadius: "20px",
  },
  "& .MuiPaper-root": { maxWidth: "400px" },
  "& .MuiDialogActions-root": {},
}));

export default function CreateFaqModal({
  open,
  setOpen,
  isEdit,
  getFaqs,
  editableFaq,
  isView,
  setPage,
}) {
  const [loading, setLoading] = useState(false);
  const classes = getSx(true);
  const dispatch = useDispatch();
  const initialValues = {
    questionEn: editableFaq?.question?.en || "",
    answerEn: editableFaq?.answer?.en || "",
    questionHi: editableFaq?.question?.hi || "",
    answerHi: editableFaq?.answer?.hi || "",
    questionFr: editableFaq?.question?.fr || "",
    answerFr: editableFaq?.answer?.fr || "",
    questionEs: editableFaq?.question?.es || "",
    answerEs: editableFaq?.answer?.es || "",
  };

  const validationSchema = Yup.object({
    questionEn: Yup.string().required("Question is required"),
    questionHi: Yup.string().required("Question is required"),
    questionFr: Yup.string().required("Question is required"),
    questionEs: Yup.string().required("Question is required"),
    answerEn: Yup.string().required("Answer is required"),
    answerHi: Yup.string().required("Answer is required"),
    answerFr: Yup.string().required("Answer is required"),
    answerEs: Yup.string().required("Answer is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          question: {
            en: values.questionEn,
            hi: values.questionHi,
            fr: values.questionFr,
            es: values.questionEs,
          },
          answer: {
            en: values.answerEn,
            hi: values.answerHi,
            fr: values.answerFr,
            es: values.answerEs,
          },
        };
        const res = isEdit
          ? await editFaq({ ...payload, faqId: editableFaq?.id })
          : await addFaq(payload);
        !isEdit && setPage(1);
        isEdit ? await getFaqs() : await getFaqs(1);
        dispatch(showToast({ color: "success", msg: res.data.message?.en }));
        closeModalHandler();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        // dispatch(showToast({ color: "error", msg: "Something went wrong" }));
      }
    },
  });

  const handleChange = (e) => {
    formik.setFieldValue(e.target.name, e.target.value);
  };

  const closeModalHandler = () => {
    setOpen(false);
  };

  const getDisabled = () => {
    const qn = formik?.values?.question;
    const an = formik?.values?.answer;
    if (isEdit)
      return (
        !qn || !an || (editableFaq.question === qn && editableFaq.answer === an)
      );
    else return !qn || !an;
  };

  return (
    <CustomDialog
      open={open}
      keepMounted
      onClose={() => setOpen(false)}
      TransitionComponent={Transition}
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "600px",
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      {loading && <Spinner />}
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ padding: "8px 20px" }}>
          <Typography
            sx={{
              fontSize: "20px",
              minWidth: "300px",
              textAlign: "center",
            }}
          >
            {isEdit ? "Edit FAQ" : "Add FAQ"}
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            padding={{ xs: "0", md: "0 20px" }}
          >
            {loading && <Spinner />}
            <Typography
              sx={{
                fontSize: "18px",
                minWidth: "300px",
              }}
            >
              Question<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box
              borderRadius="40px"
              display="flex"
              marginBottom="15px"
              flexDirection="column"
              justifyContent="center"
            >
              <Box
                sx={{
                  marginBottom: "10px",
                }}
              >
                <CustomTextField
                  type="text"
                  name="questionEn"
                  placeholder="Question in english"
                  value={formik.values.questionEn}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.questionEn &&
                    Boolean(formik.errors.questionEn)
                  }
                  errorMessage={
                    formik.touched.questionEn && formik.errors.questionEn
                  }
                />
              </Box>
              <Box
                sx={{
                  marginBottom: "10px",
                }}
              >
                <CustomTextField
                  type="text"
                  name="questionHi"
                  placeholder="Question in hindi"
                  value={formik.values.questionHi}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.questionHi &&
                    Boolean(formik.errors.questionHi)
                  }
                  errorMessage={
                    formik.touched.questionHi && formik.errors.questionHi
                  }
                />
              </Box>
              <Box
                sx={{
                  marginBottom: "10px",
                }}
              >
                <CustomTextField
                  type="text"
                  name="questionFr"
                  placeholder="Question in french"
                  value={formik.values.questionFr}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.questionFr &&
                    Boolean(formik.errors.questionFr)
                  }
                  errorMessage={
                    formik.touched.questionFr && formik.errors.questionFr
                  }
                />
              </Box>
              <CustomTextField
                type="text"
                name="questionEs"
                placeholder="Question in spanish"
                value={formik.values.questionEs}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.questionEs && Boolean(formik.errors.questionEs)
                }
                errorMessage={
                  formik.touched.questionEs && formik.errors.questionEs
                }
              />
            </Box>
            <Typography
              sx={{
                fontSize: "18px",
                minWidth: "300px",
                maxWidth: "400px",
              }}
            >
              Answer<span style={{ color: "red" }}>*</span>
            </Typography>
            <Box
              borderRadius="40px"
              display="flex"
              marginBottom="15px"
              flexDirection="column"
              justifyContent="center"
            >
              <CustomTextArea
                type="text"
                name="answerEn"
                from="faq"
                placeholder="Answer in English"
                value={formik.values.answerEn}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.answerEn && Boolean(formik.errors.answerEn)
                }
                errorMessage={formik.touched.answerEn && formik.errors.answerEn}
              />
              <CustomTextArea
                type="text"
                name="answerHi"
                from="faq"
                placeholder="Answer in hindi"
                value={formik.values.answerHi}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.answerHi && Boolean(formik.errors.answerHi)
                }
                errorMessage={formik.touched.answerHi && formik.errors.answerHi}
              />
              <CustomTextArea
                type="text"
                name="answerFr"
                from="faq"
                placeholder="Answer in french"
                value={formik.values.answerFr}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.answerFr && Boolean(formik.errors.answerFr)
                }
                errorMessage={formik.touched.answerFr && formik.errors.answerFr}
              />
              <CustomTextArea
                type="text"
                name="answerEs"
                from="faq"
                placeholder="Answer in spanish"
                value={formik.values.answerEs}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.answerEs && Boolean(formik.errors.answerEs)
                }
                errorMessage={formik.touched.answerEs && formik.errors.answerEs}
              />
            </Box>
            <DialogActions
              disableSpacing={false}
              sx={{ ...classes.buttonWrapper, justifyContent: "center" }}
            >
              {!isView && (
                <Button
                  style={{
                    marginTop: 0,
                    width: "50%",
                    minWidth: "150px",
                    maxWidth: "150px",
                  }}
                  className="theme-btn-contained theme-btn theme-auth-btn"
                  label={isEdit ? "Update" : "Save"}
                  variant="contained"
                  color="primary"
                  typeButton="submit"
                  //   disabled={getDisabled()}
                />
              )}
              <Button
                style={{
                  marginTop: 0,
                  width: "50%",
                  minWidth: "150px",
                  maxWidth: "150px",
                }}
                className="theme-btn-contained theme-btn theme-auth-btn"
                label={isView ? "Close" : "Cancel"}
                variant="contained"
                color="primary"
                onClick={closeModalHandler}
              />
            </DialogActions>
          </Box>
        </DialogContent>
      </form>
    </CustomDialog>
  );
}
